import { useEffect, useState } from 'react';
import { makeStyles, TextField, Typography, Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import RoundedButton from 'components/Shared/RoundedButton';
import Modal from 'components/Shared/Modal';
import Loader from 'components/Shared/Loader';
import {
  removeUserFromAllAccounts,
  fetchUserWithAccounts,
  REQUEST_STATUS,
  resetUserAccountManagement,
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../../../redux_store';

const useStyles = makeStyles((theme) => ({
  modal: {
    width: '100%',
    minWidth: 480,
    maxWidth: 680,
    height: 'auto',
    maxHeight: '75vh',
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      minWidth: 420,
      maxWidth: 620,
      maxHeight: '80vh',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 320,
      maxWidth: '95vw',
    },
  },
  content: {
    flex: 1,
    padding: theme.spacing(2, 3),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    overflow: 'auto',
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: '#2d3748',
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.3,
    [theme.breakpoints.down('md')]: {
      fontSize: 16,
      marginBottom: theme.spacing(1),
    },
  },
  criticalWarning: {
    background: 'linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%)',
    border: '2px solid #fc8181',
    borderRadius: 10,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    fontSize: 16,
    fontWeight: 500,
    color: '#742a2a',
    lineHeight: 1.3,
    textAlign: 'center',
    width: '100%',
    boxShadow: '0 2px 8px rgba(252, 129, 129, 0.15)',
  },
  loaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
    gap: theme.spacing(1.5),
  },
  loaderText: {
    fontSize: 14,
    color: '#4a5568',
    fontWeight: 500,
  },
  footer: {
    background: '#f7fafc',
    padding: theme.spacing(2, 3),
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)',
    width: '100%',
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 90,
    height: 40,
  },
  deleteButton: {
    backgroundColor: '#dc3545',
    borderColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#c82333',
      borderColor: '#bd2130',
    },
    '&:disabled': {
      backgroundColor: '#6c757d',
      borderColor: '#6c757d',
    },
  },
  confirmationInput: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  twoColumnContainer: {
    display: 'flex',
    gap: theme.spacing(1.5),
    width: '100%',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  userInfoColumn: {
    flex: '0 0 45%',
    background: 'rgba(227, 242, 253, 0.7)',
    borderRadius: 6,
    padding: theme.spacing(1, 1.25),
    border: '1px solid #bbdefb',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      textAlign: 'center',
    },
  },
  accountsColumn: {
    flex: '1 1 55%',
    background: '#f8f9fa',
    borderRadius: 6,
    padding: theme.spacing(1, 1.25),
    border: '1px solid #e9ecef',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      textAlign: 'center',
    },
  },
  accountsList: {
    marginTop: theme.spacing(0.5),
  },
  accountItem: {
    fontSize: '0.8rem',
    color: '#444',
    padding: theme.spacing(0.25, 0),
    borderBottom: '1px solid #e0e0e0',
    position: 'relative',
    paddingLeft: theme.spacing(2),
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(0.5),
      top: '50%',
      transform: 'translateY(-50%)',
      width: 4,
      height: 4,
      borderRadius: '50%',
      backgroundColor: '#666',
    },
  },
  showMoreItem: {
    fontSize: '0.8rem',
    color: '#1976d2',
    padding: theme.spacing(0.25, 0),
    cursor: 'pointer',
    borderBottom: 'none',
    '&:hover': {
      color: '#1565c0',
      textDecoration: 'underline',
    },
  },
  tooltip: {
    backgroundColor: '#333',
    color: 'white',
    fontSize: '0.75rem',
    maxWidth: 300,
    padding: theme.spacing(1),
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  tooltipAccountItem: {
    position: 'relative',
    paddingLeft: theme.spacing(1.5),
    fontSize: '0.75rem',
    lineHeight: 1.4,
    '&:before': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(0.25),
      top: '50%',
      transform: 'translateY(-50%)',
      width: 3,
      height: 3,
      borderRadius: '50%',
      backgroundColor: 'white',
    },
  },
  userInfoText: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#1565c0',
    marginBottom: theme.spacing(0.25),
  },
  userEmailText: {
    fontSize: '0.75rem',
    color: '#666',
    fontWeight: 400,
  },
  stepIndicator: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#ccc',
    '&.active': {
      background: '#dc3545',
    },
  },
}));

const RemoveUserModal = ({
                           open,
                           onClose,
                           userUuid,
                           currentAccountUuid,
                           onUserDeleted,
                         }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { en: { btn, titles } } = useSelector((state) => state.i18n);
  const { isDistributionSponsor } = useSelector((state) => state.user);

  const {
    managedUser,
    userAccounts,
    managedUserStatus,
  } = useSelector((state) => state.userAccountManagement);

  const [isDeleting, setIsDeleting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // Step 1 or 2
  const [confirmationText, setConfirmationText] = useState('');
  const CONFIRMATION_WORD = 'DELETE';

  // Fetch user data when modal opens
  useEffect(() => {
    if (open && userUuid && currentAccountUuid) {
      dispatch(fetchUserWithAccounts({
        userUuid,
        accountUuid: currentAccountUuid,
      }));
    }
  }, [open, userUuid, currentAccountUuid, dispatch]);

  // Cleanup when modal closes
  useEffect(() => {
    if (!open) {
      dispatch(resetUserAccountManagement());
      setIsDeleting(false);
      setCurrentStep(1);
      setConfirmationText('');
    }
  }, [open, dispatch]);

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  const handleProceedToStep2 = () => {
    setCurrentStep(2);
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setConfirmationText('');
  };

  const handleDeleteUser = async () => {
    if (!managedUser || !userAccounts || userAccounts.length === 0) {
      return;
    }

    setIsDeleting(true);

    try {
      // Extract all account IDs
      const accountIds = userAccounts.map(account => account.accountUUID);

      // Delete user from all accounts with single request
      const result = await dispatch(removeUserFromAllAccounts({
        accountIds,
        userUuid: managedUser.uuid,
      }));

      if (removeUserFromAllAccounts.fulfilled.match(result)) {
        // Success - show notification, close modal and trigger parent callback
        dispatch(enqueueSnackbar({
          message: `User ${managedUser.firstName} ${managedUser.lastName} has been successfully deleted from all accounts.`,
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }));

        onClose();
        if (onUserDeleted) {
          onUserDeleted();
        }
      } else {
        // Error occurred
        setIsDeleting(false);
      }
    } catch (error) {
      // Error handling is done by Redux thunks
      setIsDeleting(false);
    }
  };

  const isLoading = managedUserStatus === REQUEST_STATUS.loading ||
    (managedUserStatus === REQUEST_STATUS.idle && !managedUser);

  const accountsCount = userAccounts?.length || 0;
  const isConfirmationValid = confirmationText.toUpperCase() === CONFIRMATION_WORD;

  // Step 1 Content: Show user info and accounts list
  const renderStep1 = () => (
    <>
      <div className={classes.twoColumnContainer}>
        {/* Left Column - User Info */}
        {managedUser && (
          <div className={classes.userInfoColumn}>
            <Typography variant="subtitle2" style={{
              marginBottom: 8,
              fontWeight: 600,
              fontSize: '0.8rem',
              color: '#1565c0',
            }}>
              User Information:
            </Typography>
            <div className={classes.userInfoText}>
              {managedUser.firstName} {managedUser.lastName}
            </div>
            <div className={classes.userEmailText}>
              {managedUser.email}
            </div>
          </div>
        )}

        {/* Right Column - Accounts */}
        {userAccounts && userAccounts.length > 0 && (
          <div className={classes.accountsColumn}>
            <Typography variant="subtitle2" style={{
              marginBottom: 4,
              fontWeight: 600,
              fontSize: '0.8rem',
              color: '#555',
            }}>
              {isDistributionSponsor
                ? `${accountsCount} Account${accountsCount > 1 ? 's' : ''}:`
                : 'Current Account:'}
            </Typography>
            <div className={classes.accountsList}>
              {userAccounts.slice(0, 4).map((account, index) => (
                <div key={account.accountUUID || index} className={classes.accountItem}>
                  {account.accountBusinessName || account.businessName || 'Unknown Account'}
                </div>
              ))}
              {userAccounts.length > 4 && (
                <Tooltip
                  title={
                    <div className={classes.tooltipContent}>
                      <strong>All {accountsCount} Accounts:</strong>
                      {userAccounts.map((account, index) => (
                        <div key={account.accountUUID || index}
                             className={classes.tooltipAccountItem}>
                          {account.accountBusinessName || account.businessName || 'Unknown Account'}
                        </div>
                      ))}
                    </div>
                  }
                  classes={{ tooltip: classes.tooltip }}
                  placement="top"
                  arrow
                >
                  <div className={classes.showMoreItem}>
                    + {userAccounts.length - 4} more account{userAccounts.length - 4 > 1 ? 's' : ''}
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={classes.criticalWarning}>
        <strong>Critical Action:</strong>
        {isDistributionSponsor
          ? ' User will be permanently removed from all accounts and lose all access.'
          : ' User will be permanently removed from this account and lose all access.'}
      </div>
    </>
  );

  // Step 2 Content: Confirmation with DELETE input
  const renderStep2 = () => (
    <>
      <div className={classes.criticalWarning}>
        <strong>Irreversible Action:</strong>
        <br />
        {isDistributionSponsor
          ? <>User <strong>{managedUser?.firstName} {managedUser?.lastName}</strong> will be
            permanently removed from {accountsCount} account{accountsCount > 1 ? 's' : ''} and will
            no longer appear in any user lists.</>
          : <>User <strong>{managedUser?.firstName} {managedUser?.lastName}</strong> will be
            permanently removed from this account and will no longer appear in the user list.</>}
        <br /><br />
        To proceed, type <strong>delete</strong> in the field below to confirm authorization.
      </div>

      <TextField
        className={classes.confirmationInput}
        variant="outlined"
        fullWidth
        placeholder='Type "delete" to confirm'
        value={confirmationText}
        onChange={(e) => setConfirmationText(e.target.value)}
        autoFocus
        error={confirmationText.length > 0 && !isConfirmationValid}
        helperText={confirmationText.length > 0 && !isConfirmationValid ? 'Please type delete exactly' : ''}
      />
    </>
  );

  return (
    <Modal
      open={open}
      classes={{ paper: classes.modal }}
      title={titles.deleteUser}
      secondTitle={
        currentStep === 1 ?
          userAccounts.length > 1
            ? titles.deleteUserSubtitle2
            : titles.deleteUserSubtitle
          : titles.deleteUserFinalTitle
      }
      handleClose={handleClose}
    >
      <div className={classes.content}>
        {isLoading ? (
          <div className={classes.loaderContainer}>
            <Loader />
            <div className={classes.loaderText}>{titles.deleteUserLoading}</div>
          </div>
        ) : (
          currentStep === 1 ? renderStep1() : renderStep2()
        )}
      </div>

      <div className={classes.footer}>
        {currentStep === 1 ? (
          <>
            <RoundedButton
              onClick={handleClose}
              disabled={isDeleting}
              className={classes.actionButton}
            >
              {btn.cancel}
            </RoundedButton>
            <RoundedButton
              onClick={handleProceedToStep2}
              disabled={isLoading || !managedUser}
              className={`${classes.actionButton} ${classes.deleteButton}`}
            >
              {btn.continue}
            </RoundedButton>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '16px' }}>
              <RoundedButton
                onClick={handleClose}
                disabled={isDeleting}
                className={classes.actionButton}
              >
                {btn.cancel}
              </RoundedButton>
              <RoundedButton
                onClick={handleBackToStep1}
                disabled={isDeleting}
                className={classes.actionButton}
              >
                {btn.back}
              </RoundedButton>
            </div>
            <RoundedButton
              onClick={handleDeleteUser}
              disabled={!isConfirmationValid || isDeleting}
              loading={isDeleting}
              className={`${classes.actionButton} ${classes.deleteButton}`}
            >
              {isDeleting ? btn.deleting : btn.permanentlyDeleteUser}
            </RoundedButton>
          </>
        )}
      </div>
    </Modal>
  );
};

RemoveUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userUuid: PropTypes.string,
  currentAccountUuid: PropTypes.string,
  onUserDeleted: PropTypes.func,
};

export default RemoveUserModal;
